import type { OwnerAttribution } from '@/lib/ownerAttribution';
import type { Sport } from '@/views/ValueDashboard/types';

// One position on the org account, attributed to the trader who owns it
// (from /org/positions-by-trader — the relay's per-trader fills ledger).
export interface OrgPositionRow {
  userId: string;
  owner: OwnerAttribution;
  venue: 'kalshi' | 'polymarket';
  ticker: string;
  side: 'yes' | 'no';
  qty: number;
  avgCents: number;
  lastFillAt: string | null;
  costBasisCents: number;
  feesPaidCents?: number;
  openFeesCents?: number;
  totalBoughtCount?: number;
  totalSoldCount?: number;
  totalBuyCostCents?: number;
  sport: Sport | null;
}

export interface ReconciledOrgLifetimeSource {
  totalBoughtCount: number;
  totalSoldCount: number;
  totalBuyCostCents: number;
}

/** OrgDetail mixes local ledger rows with remote and residual venue rows. Only
 * local rows carry authoritative lifetime totals, and even those must balance
 * bought − sold back to the held quantity before the #91 strip is allowed to
 * render. This is deliberately fail-closed: missing, non-finite, negative,
 * orphan-sell, or drifted evidence returns an empty source, which the shared
 * fullPositionAnchor renders as dashes rather than inventing history. */
export function reconciledOrgLifetimeSource(
  p: Pick<
    OrgPositionRow,
    'qty' | 'totalBoughtCount' | 'totalSoldCount' | 'totalBuyCostCents'
  >,
): Partial<ReconciledOrgLifetimeSource> {
  const bought = p.totalBoughtCount;
  const sold = p.totalSoldCount;
  const buyCost = p.totalBuyCostCents;
  if (
    typeof bought !== 'number' ||
    typeof sold !== 'number' ||
    typeof buyCost !== 'number' ||
    !Number.isFinite(bought) ||
    !Number.isFinite(sold) ||
    !Number.isFinite(buyCost) ||
    !Number.isFinite(p.qty) ||
    bought <= 0 ||
    sold < 0 ||
    buyCost < 0 ||
    p.qty <= 0 ||
    Math.abs(bought - sold - p.qty) > 1e-6
  ) {
    return {};
  }
  return {
    totalBoughtCount: bought,
    totalSoldCount: sold,
    totalBuyCostCents: buyCost,
  };
}

// Stable React key for an org position row, unique per (owner, venue, market,
// side). The org view is the one surface with an OWNER axis, so the owner must
// be in the key — otherwise two traders holding the same Polymarket market+side
// collide and an owner-filter change can't reconcile, leaving a stale
// cross-owner card in the Card/Mini/Compact grids (#70). (userId, venue,
// ticker, side) is unique within a trader — it's the position_states primary
// key — so no array index is needed to disambiguate.
export function orgRowKey(p: OrgPositionRow): string {
  return `${p.userId}-${p.venue}-${p.ticker}-${p.side}`;
}
